import React, { Component } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import Amplify, { Auth } from 'aws-amplify';

import MuiPickersUtilsProvider from 'material-ui-pickers/utils/MuiPickersUtilsProvider';
import MomentUtils from 'material-ui-pickers/utils/moment-utils';

import AWSAppSyncClient from "aws-appsync";
import { Rehydrated } from 'aws-appsync-react';
import { ApolloProvider } from 'react-apollo';

import Constants from './Constants';
import './App.css';

import PropTypes from 'prop-types';
import { MuiThemeProvider, createMuiTheme, withStyles } from '@material-ui/core/styles';
import green from '@material-ui/core/colors/green';
import deepPurple from '@material-ui/core/colors/deepPurple';
import lightBlue from '@material-ui/core/colors/lightBlue';
import red from '@material-ui/core/colors/red';

import AppToolbar from './Components/Presentation/_Shared/Layout/AppToolbar';
import AppSidebar from './Components/_Shared/AppSidebar';
import AppRouting from './AppRouting';
import Snackbar from './Components/Presentation/_Shared/Snackbar';

import Authentication from './Components/Authentication';

Amplify.configure({
  ...Constants.Auth,
  ...Constants.API,
})
Amplify.Analytics.disable()

// All the following keys are optional.
// We try our best to provide a great default value.
const theme = createMuiTheme({
  palette: {
    primary: deepPurple,
    secondary: {
      main: '#26A69A'
    },
    info: {
      light: lightBlue[50],
      main: lightBlue[500],
      dark: lightBlue[800]
    },
    error: {
      light: red[50],
      main: red[500],
      dark: red[800]
    },
    success: {
      light: green[50],
      main: green[500],
      dark: green[800]
    },
    // Used by `getContrastText()` to maximize the contrast between the background and
    // the text.
    contrastThreshold: 3,
    // Used to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.2,
  },
  overrides: {
    MuiGrid: {},
    MuiTableRow: {
      head: { height: 42 },
      root: { height: 42 }
    },
    MuiTableCell: {
      root: { padding: '0 56px 0 24px' }
    }
  }
});

const styles = theme => ({
  root: {
    flexGrow: 1,
    zIndex: 1,
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
  },
  content: {
    marginTop: 50,
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  }
});

class App extends Component {

  constructor(props) {
    super(props);
    this.state = {
      authState: this.props.authState,
      applicationAlert: null,
      sideBarOpen: false
    };
  }

  openSideBar = () => {
    this.setState({ sideBarOpen: true })
  }

  closeSideBar = () => {
    this.setState({ sideBarOpen: false })
  }

  showAlert = (variant, message) => {
    this.setState({ applicationAlert: { variant, message } })
  }

  handleAlertClose = () => {
    this.setState({ applicationAlert: null });
  }

  render() {
    const { classes } = this.props;
    const { sideBarOpen, applicationAlert } = this.state;
    return (
      <MuiThemeProvider theme={theme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <div className={classes.root}>
            {
              applicationAlert &&
              <Snackbar data={applicationAlert} onClose={this.handleAlertClose} />
            }
            {
              <Router>
                <React.Fragment>
                  <AppToolbar sideBarOpen={sideBarOpen} onSignOut={this.props.onSignOut} handleSideBarOpen={this.openSideBar} />
                  <AppSidebar sideBarOpen={sideBarOpen} onSignOut={this.props.onSignOut} handleSideBarClose={this.closeSideBar} />
                  <div className={classes.content}>
                    <AppRouting onAlert={this.showAlert} />
                  </div>
                </React.Fragment>
              </Router>
            }
          </div>
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
    )
  }
}

App.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};

class AppWithProvider extends React.Component {

  state = {
    jwtToken: '',
    authState: null,
    expiry: new Date().getTime(),
    graphqlClient: null
  }
  signOut = () => {
    this.setState({ authState: 'signedOut' });
  }
  componentDidMount() {
    this.getGraphqlClient();
  }

  handleAuthStateChange = authState => {
    this.setState({ authState });
  }

  getGraphqlClient = () => {
    Auth.currentSession().then(x => {
      const idToken = x.getIdToken();
      const jwtToken = idToken.getJwtToken()
      this.setState({
        authState: 'signedIn',
        jwtToken, expiry: idToken.getExpiration(),
        graphqlClient: new AWSAppSyncClient({
          url: Constants.API.aws_appsync_graphqlEndpoint,
          region: Constants.API.aws_appsync_region,
          auth: {
            //Amazon Cognito user pools using AWS Amplify
            type: Constants.API.aws_appsync_authenticationType,
            jwtToken: jwtToken,
          },
          disableOffline: true,
          complexObjectsCredentials: () => Auth.currentCredentials()
        })
      });
    }).catch(err => this.setState({ authState: 'signIn' }));
  }

  render() {
    const currentDate = new Date().getTime();
    const { graphqlClient, expiry, authState } = this.state;
    if (authState !== null) {
      if (authState === 'signedIn') {
        if (graphqlClient === null || expiry > new Date(currentDate + 5 * 60000).getTime()) {
          this.getGraphqlClient();
          return null
        }
        return <ApolloProvider client={graphqlClient}>
          <Rehydrated>
            <App {...this.props} authState={authState} onSignOut={this.signOut} />
          </Rehydrated>
        </ApolloProvider>
      } else {
        return <MuiThemeProvider theme={theme} ><Authentication onStateChange={this.handleAuthStateChange} /></MuiThemeProvider>;
      }
    }
    else {
      return null;
    }
  }
}

export default withStyles(styles, { withTheme: true })(AppWithProvider);


// WEBPACK FOOTER //
// ./src/App.js